import React from "react";
import { UploadStatus } from "lib/types/UploadStatus";
import { AssetIngressModel } from "lib/types/assets/AssetIngressModel";
import { getUploadStatusLabel } from "lib/actions/getUploadStatusLabel";
import Chip from "./Chip";

type Status = UploadStatus | AssetIngressModel["status"];

function getStatusLabel(status: Status, processingDetailsMessage?: string) {
  switch (status) {
    case "none":
    case "uploading":
      return "Uploading";
    case "awaitingProcessing":
      return "Awaiting Processing";
    case "processing":
      return processingDetailsMessage ?? "Processing";
    case "done":
      return "Complete";
    case "error":
      return "Unsuccessful";
    case "partialError":
      return "Partial Error";
  }
  return getUploadStatusLabel(status);
}

const colorMap = new Map<
  Status,
  { text: string; bg: string; className?: string }
>([
  ["none", { text: null, bg: null, className: "text-gray-500" }],
  [
    UploadStatus.preparing,
    { text: "#a747e7", bg: "#c77afc1c", className: "text-purple-500" },
  ],
  [
    UploadStatus.queued,
    { text: "#a747e7", bg: "#c77afc1c", className: "text-purple-500" },
  ],
  [
    UploadStatus.paused,
    { text: "#556172", bg: "#F3F4F6", className: "text-gray-500" },
  ],
  [
    UploadStatus.uploading,
    { text: "#3079f0", bg: "#4a8ffc10", className: "text-sky-500" },
  ],
  [
    UploadStatus.complete,
    { text: "#9f962e", bg: "#feee2a5c", className: "text-yellow-500" },
  ],
  [
    UploadStatus.error,
    { text: "#ff4239", bg: "#ffc9c7", className: "text-red-500" },
  ],
  [
    "awaitingProcessing",
    { text: "#EAB308", bg: "#feee2a5c", className: "text-yellow-500" },
  ],
  [
    "processing",
    { text: "#F59E0B", bg: "#ffe8c7", className: "text-amber-500" },
  ],
  ["done", { text: "#3fd95c", bg: "#d4fedf", className: "text-green-500" }],
  ["error", { text: "#ff4239", bg: "#ffc9c7", className: "text-red-500" }],
  [
    "partialError",
    { text: "#ff4239", bg: "#ffc9c7", className: "text-red-500" },
  ],
]);

const percentageStatuses = new Set<Status>([
  UploadStatus.paused,
  UploadStatus.uploading,
]);

export default function IngressStatusChip(p: {
  status: Status;
  progressPercent?: number;
  processingDetailsMessage?: string;
  // This is a temporary prop to determine whether we use the new ingress modal v2 design
  // We will remove it along with the 'feature-ingress-modal' feature flag.
  v2?: boolean | null;
}) {
  const colors = colorMap.get(p.status) ?? colorMap.get("none");

  return p.v2 ? (
    <div className={colors.className}>
      <span className="text-xs font-light">
        {getStatusLabel(p.status, p.processingDetailsMessage)}
      </span>
      {p.progressPercent >= 0 && percentageStatuses.has(p.status) && (
        <span className="text-xs font-light">
          {p.status === UploadStatus.paused && <>&nbsp;at</>}
          &ensp;&middot;&ensp;{p.progressPercent}%
        </span>
      )}
    </div>
  ) : (
    <Chip textColor={colors.text} bgColor={colors.bg} rounded={true}>
      <div className="p-0.5">
        <span>{getStatusLabel(p.status)}</span>
        {p.progressPercent >= 0 && percentageStatuses.has(p.status) && (
          <span>
            {p.status === UploadStatus.paused && <>&nbsp;at</>}
            &nbsp;{p.progressPercent}%
          </span>
        )}
      </div>
    </Chip>
  );
}
